/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
} from '@mui/material'
import { CommonTextField } from './CommonTextField'
import { getComputedWidth } from './utils'
import { MyLocationIcon } from '@/icons'
import { CommonButton } from './CommonButton'
import { CommonLoader } from './CommonLoader'

// Keep a single promise to avoid loading the Google script multiple times
let googleMapsApiPromise = null

const loadGoogleMapsPlaces = () => {
  if (typeof window !== 'undefined' && window.google?.maps?.places) {
    return Promise.resolve(true)
  }

  if (googleMapsApiPromise) return googleMapsApiPromise

  const apiKey =
    process.env.REACT_APP_GOOGLE_MAP_KEY ||
    (typeof window !== 'undefined' ? window.__GOOGLE_API_KEY__ : undefined)

  if (!apiKey && process.env.NODE_ENV !== 'production') {
    console.warn(
      '[CommonAddress] Google API key not found. Set REACT_APP_GOOGLE_MAPS_API_KEY or REACT_APP_GOOGLE_MAP_API_KEY, pass googleApiKey prop, or set window.__GOOGLE_API_KEY__ before load.'
    )
    return Promise.resolve(false)
  }

  googleMapsApiPromise = new Promise(resolve => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => {
      if (typeof window !== 'undefined' && window.google?.maps?.places)
        resolve(true)
      else resolve(false)
    }

    script.onerror = event => {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '[CommonAddress] Failed to load Google Maps script',
          event
        )
      }
      resolve(false)
    }
    document.head.appendChild(script)
  })

  return googleMapsApiPromise
}

const extractAddressComponent = (components, type) => {
  const comp = components.find(c => c.types?.includes(type))
  return comp ? comp.long_name : ''
}

const getStructuredAddressFromPlaceResult = place => {
  const components = place.address_components || []
  const city = extractAddressComponent(components, 'locality')
  const county = extractAddressComponent(
    components,
    'administrative_area_level_2'
  )

  return {
    formatted_address: place.formatted_address || '',
    address1: extractAddressComponent(components, 'street_number') || '',
    address2: extractAddressComponent(components, 'route') || '',
    zip_code: extractAddressComponent(components, 'postal_code') || '',
    city: city || county || '',
    state:
      extractAddressComponent(components, 'administrative_area_level_1') || '',
    county: county || '',
    country: extractAddressComponent(components, 'country') || '',
    latitude: place.geometry?.location?.lat
      ? place.geometry.location.lat().toString()
      : '',
    longitude: place.geometry?.location?.lng
      ? place.geometry.location.lng().toString()
      : '',
  }
}

export const CommonAddress = ({
  id,
  label,
  value = {},
  error,
  width,
  helperText,
  fullWidth = true,
  readOnly = false,
  required = false,
  country = 'US',
  setValue,
  onChange,
  onPlaceChange,
  inputProps,
  googleApiKey,
  ...muiProps
}) => {
  const [loaded, setLoaded] = useState(false)
  const [predictions, setPredictions] = useState([])
  const [openList, setOpenList] = useState(false)
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false)
  const [isLocating, setIsLocating] = useState(false)
  const [isCurrentLocationSelected, setIsCurrentLocationSelected] =
    useState(false)
  const [inputDisplay, setInputDisplay] = useState('')
  const [hasSelectedPlace, setHasSelectedPlace] = useState(false)

  const placesServiceRef = useRef(null)
  const placesDetailsServiceRef = useRef(null)
  const geocoderRef = useRef(null)
  const selectionMadeRef = useRef(false)

  const isObject = item =>
    item && typeof item === 'object' && !Array.isArray(item)

  const getDisplayFromValue = val => {
    if (!val || !isObject(val)) return ''
    return val.formatted_address || ''
  }

  useEffect(() => {
    let isMounted = true
    loadGoogleMapsPlaces(googleApiKey).then(success => {
      if (!isMounted) return
      if (success) setLoaded(true)
    })
    return () => {
      isMounted = false
    }
  }, [])

  // Keep the visible input in sync with parent-provided value
  useEffect(() => {
    const next = getDisplayFromValue(value)
    setInputDisplay(next)
    // If value has formatted_address, it means a place was selected
    const hasPlace = !!(value && value.formatted_address)
    setHasSelectedPlace(hasPlace)
    selectionMadeRef.current = hasPlace
  }, [value])

  useEffect(() => {
    if (!loaded || !window.google?.maps) return
    if (!placesServiceRef.current) {
      placesServiceRef.current =
        new window.google.maps.places.AutocompleteService()
    }
    if (!placesDetailsServiceRef.current) {
      // Create a dummy map element for PlacesService (it requires a map)
      const dummyMapDiv = document.createElement('div')
      const dummyMap = new window.google.maps.Map(dummyMapDiv)
      placesDetailsServiceRef.current =
        new window.google.maps.places.PlacesService(dummyMap)
    }
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder()
    }
  }, [loaded])

  const requestPredictions = input => {
    if (!loaded || !placesServiceRef.current) return
    if (!input || input.trim().length < 3) {
      setPredictions([])
      setOpenList(false)
      return
    }

    setIsLoadingPredictions(true)
    placesServiceRef.current.getPlacePredictions(
      {
        input,
        componentRestrictions: country ? { country } : undefined,
        language: 'en',
      },
      (preds, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          Array.isArray(preds)
        ) {
          setPredictions(preds)
          setOpenList(true)
        } else {
          setPredictions([])
          setOpenList(false)
        }
        setIsLoadingPredictions(false)
      }
    )
  }

  const handleInput = newValue => {
    setIsCurrentLocationSelected(false)
    setInputDisplay(newValue)
    // When user types, mark that no place is selected yet
    setHasSelectedPlace(false)
    selectionMadeRef.current = false

    if (!loaded) return
    requestPredictions(newValue)
  }

  const handlePredictionClick = prediction => {
    const description = prediction?.description || ''
    const placeId = prediction?.place_id

    setInputDisplay(description)
    setOpenList(false)
    setHasSelectedPlace(true)
    selectionMadeRef.current = true

    // Use Places API getPlaceDetails to get exact place details
    if (!placesDetailsServiceRef.current || !placeId) {
      // Fallback to geocoding if PlacesService is not available
      if (!geocoderRef.current) return
      geocoderRef.current.geocode(
        { address: description, language: 'en' },
        (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results?.[0]) {
            const place = results[0]
            const structured = getStructuredAddressFromPlaceResult(place)
            // Use the selected description as formatted_address to preserve user selection
            structured.formatted_address = description
            if (setValue) setValue(structured)
            if (onChange) onChange(structured)
            if (onPlaceChange) onPlaceChange(structured)
          }
        }
      )
      return
    }

    placesDetailsServiceRef.current.getDetails(
      {
        placeId: placeId,
        fields: ['formatted_address', 'address_components', 'geometry', 'name'],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          const structured = getStructuredAddressFromPlaceResult(place)
          // Use the selected description as formatted_address to preserve user selection
          structured.formatted_address = description
          if (setValue) setValue(structured)
          if (onChange) onChange(structured)
          if (onPlaceChange) onPlaceChange(structured)
        } else {
          // Fallback to geocoding if getDetails fails
          if (!geocoderRef.current) return
          geocoderRef.current.geocode(
            { address: description, language: 'en' },
            (results, geocodeStatus) => {
              if (
                geocodeStatus === window.google.maps.GeocoderStatus.OK &&
                results?.[0]
              ) {
                const geocodePlace = results[0]
                const structured =
                  getStructuredAddressFromPlaceResult(geocodePlace)
                // Use the selected description as formatted_address to preserve user selection
                structured.formatted_address = description
                if (setValue) setValue(structured)
                if (onChange) onChange(structured)
                if (onPlaceChange) onPlaceChange(structured)
              }
            }
          )
        }
      }
    )
  }

  const handleUseCurrentLocation = () => {
    if (!navigator?.geolocation) return
    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      position => {
        const lat = position.coords.latitude
        const lng = position.coords.longitude

        if (!geocoderRef.current) {
          setIsLocating(false)
          return
        }

        geocoderRef.current.geocode(
          { location: { lat, lng }, language: 'en' },
          (results, status) => {
            setIsLocating(false)
            if (
              status === window.google.maps.GeocoderStatus.OK &&
              results?.[0]
            ) {
              const place = results[0]
              const description = place.formatted_address || ''
              setInputDisplay(description)
              const structured = getStructuredAddressFromPlaceResult(place)
              // Store the structured object without display property
              if (setValue) setValue(structured)
              if (onChange) onChange(structured)
              if (onPlaceChange) onPlaceChange(structured)
              setIsCurrentLocationSelected(true)
              setHasSelectedPlace(true)
              selectionMadeRef.current = true
              setPredictions([])
              setOpenList(false)
            }
          }
        )
      },
      () => setIsLocating(false),
      { maximumAge: 60000, enableHighAccuracy: true }
    )
  }

  const handleFocus = () => {
    const display = getDisplayFromValue(value)
    if (display && display.trim().length >= 3) {
      // If no predictions cached, fetch them
      if (predictions.length === 0) {
        requestPredictions(display)
      } else {
        // Otherwise just open the list with cached predictions
        setOpenList(true)
      }
    }
  }

  const handleBlur = () => {
    // Close the dropdown after a short delay to allow click events
    setTimeout(() => {
      setOpenList(false)
      // If user navigated away without selecting a place, clear the field
      // Also clear if input is empty but we have a value (user manually cleared it)
      const currentDisplay = getDisplayFromValue(value)
      const inputIsEmpty = inputDisplay.trim() === ''
      const valueExists = value && Object.keys(value).length > 0

      if (!selectionMadeRef.current) {
        if (inputIsEmpty && valueExists) {
          // User manually cleared the input, clear the value
          setInputDisplay('')
          if (setValue) setValue({})
          if (onChange) onChange({})
        } else if (
          inputDisplay.trim() !== '' &&
          inputDisplay !== currentDisplay
        ) {
          // User typed something but didn't select, clear it
          setInputDisplay('')
          if (setValue) setValue({})
          if (onChange) onChange({})
        }
      }
    }, 50)
  }

  const showPredictions = useMemo(() => {
    return loaded && openList && predictions.length > 0 && !readOnly
  }, [loaded, openList, predictions, readOnly])

  const computedWidth = getComputedWidth(width, fullWidth)

  return (
    <Box
      data-testid={id}
      sx={{ position: 'relative', width: computedWidth, minWidth: 0 }}
    >
      <CommonTextField
        {...muiProps}
        id={id}
        label={label}
        value={inputDisplay}
        error={error}
        width={width}
        fullWidth={fullWidth}
        readOnly={readOnly}
        required={required}
        onChange={handleInput}
        inputProps={inputProps}
        helperText={showPredictions ? '' : helperText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end'>
                {isLoadingPredictions || isLocating ? (
                  <CommonLoader size={16} thickness={4} />
                ) : (
                  <CommonButton
                    size='small'
                    variant='text'
                    disabled={readOnly || isCurrentLocationSelected}
                    onClick={handleUseCurrentLocation}
                    iconOnly={<MyLocationIcon fontSize='small' />}
                  />
                )}
              </InputAdornment>
            ),
          },
        }}
        labelRight={
          <CommonButton
            size='small'
            variant='text'
            sx={{ p: 0 }}
            disabled={readOnly || isCurrentLocationSelected}
            onClick={handleUseCurrentLocation}
          >
            Use My Current Location
          </CommonButton>
        }
      />

      {showPredictions && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 'calc(100% + 4px)',
            width: '100%',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 1170,
          }}
        >
          <List dense disablePadding>
            {predictions.map(prediction => (
              <ListItemButton
                key={prediction.place_id}
                onMouseDown={event => event.preventDefault()}
                onClick={() => handlePredictionClick(prediction)}
              >
                <ListItemText primary={prediction.description} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  )
}
